import { useEffect, useMemo, useState } from "react";
import { Tour } from "@NP-Webber/crm-kit/dist/next/index.js";
import { DesignOptions } from "./design/DesignOptions";
import { EVENTS, onEvent } from "./events";

/**
 * Guided tour of the new design: sidebar, search, quick actions, related settings, automations,
 * Today, messages, personal menu, tables + a live step to try the display options.
 * Opens once per user on the first visit, and again from the personal menu / help center.
 */
const KEY = (uid: string | null) => `tb-design-tour:${uid || "anon"}`;

export function DesignTour({ userId }: { userId: string | null }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(KEY(userId)) === "done";
    } catch {
      seen = true;
    }
    if (seen) return undefined;
    const t = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(t);
  }, [userId]);

  useEffect(() => onEvent(EVENTS.tour, () => setOpen(true)), []);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(KEY(userId), "done");
    } catch {
      /* storage blocked */
    }
  };

  const steps = useMemo(
    () => [
      {
        title: "ברוכים הבאים לעיצוב החדש",
        wide: true,
        body: (
          <>
            <p>אותה מערכת, אותם נתונים — בפריסה חדשה, נקייה ומהירה יותר.</p>
            <p>בסיור קצר (דקה) נראה איפה נמצא כל דבר ונכיר את אפשרויות העיצוב.</p>
          </>
        ),
      },
      {
        target: ".ck-mods",
        optional: true,
        title: "מודולים — מכירות וכספים",
        body: (
          <p>
            כל תחום עבודה הוא מודול: <b>מכירות</b> (לקוחות, מנויים וגבייה) ו<b>כספים</b> (הכנסות והוצאות, בנק,
            ספקים ומסמכים). מעבר ביניהם מכאן — והסרגל בצד מתעדכן בהתאם.
          </p>
        ),
      },
      {
        target: ".ck-sidebar__nav",
        title: "הסרגל — המסכים של המודול",
        body: (
          <ul>
            <li>
              המסכים של המודול הפעיל, מקובצים לפי נושא. <b>משרד</b> קבוע בכל המודולים.
            </li>
            <li>
              כל ההגדרות מרוכזות במקום אחד — <b>הגדרות</b> בתחתית.
            </li>
          </ul>
        ),
      },
      {
        target: ".ck-gsearch",
        optional: true,
        title: "חיפוש בכל המערכת — Ctrl+K",
        body: <p>לקוחות, משתמשים, משימות, טפסים, מסכים, הגדרות ומדריכים — מקלידים ומגיעים, מכל מקום.</p>,
      },
      {
        target: ".ck-qa",
        optional: true,
        title: "„חדש” ופעולות מהירות",
        body: <p>יצירה מהירה (לקוח, משימה, הכנסה, הוצאה…) בלי לעזוב את המסך. אפשר להצמיד כאן את הפעולות שמשתמשים בהן הכי הרבה.</p>,
      },
      {
        target: ".rs-btn",
        optional: true,
        title: "הגדרות קשורות — Ctrl+,",
        body: <p>ההגדרות שמשפיעות על המסך שבו אתם נמצאים, עם הסבר למה — וחלקן נערכות ישירות מהפאנל.</p>,
      },
      {
        target: ".atm-btn",
        optional: true,
        title: "אוטומציות",
        body: <p>מה קורה לבד במסך הזה (מיילים, משימות, דוחות) — והוספת אוטומציה מתבנית בלחיצה.</p>,
      },
      {
        target: ".tdy-btn",
        optional: true,
        title: "היום בתוכנה",
        body: <p>מה נכנס היום, מה דורש החלטה ומה המשימות שלכם — במבט אחד.</p>,
      },
      {
        target: ".ck-dt__toolbar",
        optional: true,
        title: "טבלאות",
        body: <p>בכל טבלה: חיפוש, סינון לפי כל עמודה, בחירת עמודות ותצוגות שמורות, ייצוא לאקסל ופעולות על כמה שורות יחד.</p>,
      },
      {
        target: ".ck-sidebar__act",
        optional: true,
        title: "מרכז הודעות",
        body: <p>הודעות מערכת, מה חדש, הצעות לפיתוח ודיווח על תקלה. הצ׳אט נשאר בכפתור העגול בפינה.</p>,
      },
      {
        target: ".ck-sidebar__user",
        title: "התפריט האישי",
        body: <p>אזור אישי, מצב תצוגה, החלפת ארגון ומרכז ההדרכה (F1) — ומכאן אפשר לפתוח שוב את הסיור הזה.</p>,
      },
      {
        title: "נסו את אפשרויות העיצוב",
        wide: true,
        body: (
          <div className="nd-tour-opts">
            <p>הבחירה נשמרת אצלכם ומופעלת מיד:</p>
            <DesignOptions compact />
          </div>
        ),
      },
      {
        title: "זהו, אפשר להתחיל",
        body: <p>בהצלחה! הסיור זמין תמיד בתפריט האישי ← „סיור בעיצוב”. משהו לא נוח? „דיווח על תקלה” במרכז ההודעות.</p>,
      },
    ],
    [],
  );

  return <Tour open={open} steps={steps} onClose={close} />;
}

export default DesignTour;
